import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { FileCheck2, Plus, Trash2, Braces, Upload, Download, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Templates() {
  return (
    <div>
      <PageHeader title="Templates & Paperwork Setup" subtitle="Manage notification templates and the paperwork students must complete" />
      <Tabs defaultValue="paperwork">
        <TabsList className="mb-5">
          <TabsTrigger value="paperwork" data-testid="tab-paperwork-packets"><FileCheck2 className="w-4 h-4 mr-1.5" />Paperwork Packets</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notification-templates">Notification Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="paperwork"><PaperworkPackets /></TabsContent>
        <TabsContent value="notifications"><NotificationTemplates /></TabsContent>
      </Tabs>
    </div>
  );
}

function PaperworkPackets() {
  const [packet, setPacket] = useState(null);
  const [newDoc, setNewDoc] = useState({ name: "", required: true, doc_type: "form" });
  const [name, setName] = useState("");
  const load = () => api.get("/paperwork-templates/universal").then(({ data }) => { setPacket(data); setName(data.name || ""); });
  useEffect(() => { load(); }, []);

  const addDoc = async () => {
    if (!newDoc.name.trim()) return toast.error("Enter a document name");
    try { await api.post(`/paperwork-templates/${packet.id}/items`, newDoc); setNewDoc({ name: "", required: true, doc_type: "form" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add"); }
  };
  const saveName = async () => {
    if (!name.trim()) return toast.error("Packet name cannot be empty");
    try { await api.patch(`/paperwork-templates/${packet.id}`, { name }); toast.success("Packet name saved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
  };

  if (!packet) return <Card><EmptyState text="Loading paperwork packet…" /></Card>;
  const items = packet.items || [];

  return (
    <div className="max-w-3xl">
      <Card className="p-0" data-testid="universal-packet">
        <div className="px-5 py-4 border-b bg-cc-blue/5">
          <div className="flex items-center gap-2">
            <FileCheck2 className="w-5 h-5 text-cc-blue" />
            <h3 className="font-semibold text-zinc-900">Universal Paperwork Packet</h3>
            <span className="ml-auto text-[10px] font-semibold uppercase text-cc-green">Auto-assigned</span>
          </div>
          <p className="text-sm text-zinc-500 mt-1.5">
            One packet for <b>every student</b>, all disciplines. It's automatically assigned when a facility (DOR)
            accepts a placement — the student gets a dashboard invite and sees these documents to complete.
          </p>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mb-1.5">Packet name</p>
            <div className="flex items-center gap-2 max-w-md">
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="universal-packet-name" />
              {name !== packet.name && <Button size="sm" className="bg-cc-blue text-white shrink-0" onClick={saveName} data-testid="save-universal-name">Save</Button>}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-zinc-400 font-semibold mb-2">Documents in this packet ({items.length})</p>
            {items.length === 0 ? <EmptyState text="No documents yet — add the forms students must complete." /> : (
              <div className="space-y-2" data-testid="universal-packet-items">
                {items.map((it) => <EditableItemRow key={it.id} item={it} onChange={load} />)}
              </div>
            )}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t">
              <Input value={newDoc.name} onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })} placeholder="Add a document…" data-testid="new-doc-name" />
              <Select value={newDoc.doc_type} onValueChange={(v) => setNewDoc({ ...newDoc, doc_type: v })}>
                <SelectTrigger className="w-36 h-9 shrink-0 text-xs" data-testid="new-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="form">Fill-out form</SelectItem>
                  <SelectItem value="upload">Upload document</SelectItem>
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1 text-xs text-zinc-500 shrink-0"><Checkbox checked={newDoc.required} onCheckedChange={(c) => setNewDoc({ ...newDoc, required: c })} />Req</label>
              <Button size="sm" onClick={addDoc} className="bg-cc-blue text-white shrink-0" data-testid="add-doc-btn"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function EditableItemRow({ item, onChange }) {
  const [name, setName] = useState(item.name);
  const [req, setReq] = useState(!!item.required);
  const [docType, setDocType] = useState(item.doc_type || "form");
  const [sign, setSign] = useState(!!item.sign_in_app);
  const [signText, setSignText] = useState(item.sign_body_text || "");
  const [showText, setShowText] = useState(false);
  const dirty = name !== item.name || req !== !!item.required || docType !== (item.doc_type || "form")
    || sign !== !!item.sign_in_app || signText !== (item.sign_body_text || "");
  const save = async () => {
    if (!name.trim()) return toast.error("Name cannot be empty");
    try { await api.patch(`/paperwork-items/${item.id}`, { name, required: req, doc_type: docType, sign_in_app: sign, sign_body_text: signText }); toast.success("Document updated"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
  };
  const del = async () => {
    if (!window.confirm(`Remove "${item.name}" from this packet?`)) return;
    try { await api.delete(`/paperwork-items/${item.id}`); toast.success("Document removed"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not remove"); }
  };
  return (
    <div className="flex items-center gap-2 border rounded-md p-2 flex-wrap" data-testid={`edit-item-${item.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-sm flex-1 min-w-[140px]" data-testid={`edit-item-name-${item.id}`} />
      <Select value={docType} onValueChange={setDocType}>
        <SelectTrigger className="w-32 h-8 shrink-0 text-xs" data-testid={`edit-item-type-${item.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="form">Fill-out form</SelectItem>
          <SelectItem value="upload">Upload document</SelectItem>
        </SelectContent>
      </Select>
      <label className="flex items-center gap-1 text-xs text-zinc-500 shrink-0"><Checkbox checked={req} onCheckedChange={(c) => setReq(!!c)} data-testid={`edit-item-required-${item.id}`} />Req</label>
      <label className="flex items-center gap-1 text-xs text-zinc-500 shrink-0" title="The student reads and signs this in the app — no printing or scanning">
        <Checkbox checked={sign} onCheckedChange={(c) => { setSign(!!c); if (c) setShowText(true); }} data-testid={`edit-item-sign-${item.id}`} />Sign in app
      </label>
      {sign && (
        <button type="button" onClick={() => setShowText((v) => !v)} className="text-xs text-cc-blue shrink-0" data-testid={`edit-item-sign-text-toggle-${item.id}`}>
          {showText ? "hide wording" : "wording"}
        </button>
      )}
      {dirty && <Button size="sm" className="h-8 bg-cc-blue text-white shrink-0" onClick={save} data-testid={`save-item-${item.id}`}>Save</Button>}
      <button onClick={del} className="text-zinc-400 hover:text-red-600 shrink-0" data-testid={`delete-item-${item.id}`}><Trash2 className="w-4 h-4" /></button>
      <ItemTemplateControl item={item} onChange={onChange} />
      {sign && showText && (
        <div className="w-full mt-1">
          <Textarea rows={5} value={signText} onChange={(e) => setSignText(e.target.value)} data-testid={`edit-item-sign-text-${item.id}`}
            className="text-xs font-mono" placeholder="Paste the wording the student must agree to. Leave blank to use the standard TMC confidentiality wording, or attach a PDF above and we'll stamp the signature onto it." />
          <p className="text-[11px] text-zinc-400 mt-1">If a PDF is attached, the signature is stamped onto its last page. Otherwise we generate the signed form from this wording.</p>
        </div>
      )}
    </div>
  );
}

function ItemTemplateControl({ item, onChange }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/paperwork-items/${item.id}/template`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Blank form attached"); onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not attach the form"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <span className="ml-auto flex items-center gap-2">
      {item.doc_type === "upload" ? (
        <span className="inline-flex items-center gap-1 text-xs text-cc-orange" data-testid={`item-upload-tag-${item.id}`}>
          <Upload className="w-3.5 h-3.5" />Student uploads
        </span>
      ) : (
        <>
          {item.template_file_name && (
            <a href={`${BACKEND}/api/paperwork-items/${item.id}/template`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-cc-blue text-xs" data-testid={`item-download-${item.id}`}>
              <Download className="w-3.5 h-3.5" />blank
            </a>
          )}
          <label className="inline-flex items-center gap-1 text-xs text-zinc-500 cursor-pointer hover:text-cc-blue" data-testid={`item-attach-${item.id}`}>
            <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={onFile} />
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {item.template_file_name ? "Replace" : "Attach form"}
          </label>
        </>
      )}
    </span>
  );
}

function NotificationTemplates() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [variables, setVariables] = useState([]);
  const [varOpen, setVarOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/notification-templates").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/notification-templates/variables").then(({ data }) => setVariables(data)).catch(() => {});
  }, []);

  const insertVar = (key) => setEdit((e) => ({ ...e, body: `${e.body || ""}{{${key}}}` }));

  const refreshPreview = async (draft) => {
    try {
      const { data } = await api.post(`/notification-templates/${draft.key}/preview`, { subject: draft.subject, body: draft.body });
      setPreview(data);
    } catch (e) { setPreview(null); }
  };
  const open = (t) => { setEdit(t); setPreview(null); refreshPreview(t); };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/notification-templates", { key: edit.key, subject: edit.subject, body: edit.body, active: edit.active !== false });
      toast.success("Wording saved — the next email uses it");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    try {
      const { data } = await api.post(`/notification-templates/${edit.key}/reset`);
      const next = { ...edit, subject: data.subject, body: data.body, active: true };
      setEdit(next); refreshPreview(next); load();
      toast.success("Back to the original wording");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const testSend = async () => {
    if (!testTo) return toast.error("Enter a recipient email");
    try {
      await api.post("/notification-templates/test-send", { to: testTo, key: edit.key, subject: edit.subject, body: edit.body });
      toast.success(`Test sent to ${testTo}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const grouped = rows.reduce((acc, t) => { (acc[t.audience] = acc[t.audience] || []).push(t); return acc; }, {});
  const ownVars = (edit?.variables || []);

  return (
    <div className="space-y-6">
      <Card>
        <p className="text-sm text-zinc-600">
          These are the real emails Campus Connections sends. Edit the subject and wording here and the next
          one goes out with your words — variables like <code className="text-cc-blue">{"{{facility_name}}"}</code> are
          filled in automatically. Who receives it and when is driven by the workflow, so that part isn't editable.
        </p>
      </Card>
      {Object.entries(grouped).map(([aud, items]) => (
        <Card key={aud} title={`To the ${aud.toLowerCase()}`}>
          <div className="space-y-2">
            {items.map((t) => (
              <div key={t.key} className="flex flex-wrap items-center justify-between gap-3 border rounded-md p-3" data-testid={`template-${t.key}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-800">
                    {t.name}
                    {t.customised && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-cc-blue/10 text-cc-blue rounded" data-testid={`template-customised-${t.key}`}>edited</span>}
                    {t.active === false && <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded">off</span>}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5"><b>Sent when:</b> {t.sent_when}</p>
                  <p className="text-xs text-zinc-400">Subject: “{t.subject}”</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => open(t)} data-testid={`edit-template-${t.key}`}>Edit wording</Button>
              </div>
            ))}
          </div>
        </Card>
      ))}
      {rows.length === 0 && <Card><EmptyState text="No templates." /></Card>}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto cc-scroll">
          <DialogHeader><DialogTitle>{edit?.name}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid md:grid-cols-2 gap-5">
              <div className="space-y-4">
                <div className="text-xs text-zinc-500 bg-zinc-50 rounded-md p-3 space-y-1">
                  <p><b>Sent when:</b> {edit.sent_when}</p>
                  <p><b>Sent to:</b> {edit.sent_to}</p>
                </div>
                <Field label="Subject"><Input value={edit.subject || ""} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} data-testid="template-subject" /></Field>
                <Field label="Message">
                  <Textarea rows={12} value={edit.body || ""} onChange={(e) => setEdit({ ...edit, body: e.target.value })} data-testid="template-body" className="font-mono text-xs" />
                </Field>
                <p className="text-xs text-zinc-400">
                  Leave a blank line between paragraphs. A run of <code>Label: value</code> lines becomes a neat detail table.
                </p>
                {ownVars.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="template-own-vars">
                    {ownVars.map((v) => (
                      <button key={v} type="button" onClick={() => insertVar(v)} data-testid={`own-var-${v}`}
                        className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-cc-blue/5 text-cc-blue hover:bg-cc-blue/10 transition-colors">{`{{${v}}}`}</button>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <Popover open={varOpen} onOpenChange={setVarOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="text-xs" data-testid="variables-popout-btn">
                        <Braces className="w-3.5 h-3.5 mr-1.5" />All variables
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-80 p-0 max-h-96 overflow-y-auto cc-scroll" data-testid="variables-popout">
                      <div className="p-3 border-b sticky top-0 bg-white">
                        <p className="text-sm font-semibold text-zinc-800">Merge variables</p>
                        <p className="text-xs text-zinc-400">Click any variable to add it to the message.</p>
                      </div>
                      <div className="p-2 space-y-3">
                        {variables.map((g) => (
                          <div key={g.group}>
                            <p className="text-[11px] uppercase tracking-wide text-zinc-400 px-2 mb-1">{g.group}</p>
                            {g.vars.map((v) => (
                              <button key={v.key} type="button" onClick={() => insertVar(v.key)} data-testid={`var-${v.key}`}
                                className="w-full text-left px-2 py-1.5 rounded hover:bg-cc-blue/5 transition-colors group">
                                <code className="text-xs text-cc-blue font-mono">{`{{${v.key}}}`}</code>
                                <span className="block text-[11px] text-zinc-500 group-hover:text-zinc-700">{v.desc}</span>
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => refreshPreview(edit)} data-testid="refresh-preview-btn">Update preview</Button>
                  <Button variant="ghost" size="sm" className="text-xs text-zinc-500" onClick={reset} data-testid="reset-template-btn">Reset to original</Button>
                </div>
                <div className="flex gap-2 border-t pt-3">
                  <Input placeholder="you@therapymgmt.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="test-email" />
                  <Button variant="outline" onClick={testSend} data-testid="test-send-btn">Send me a test</Button>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase text-zinc-400 mb-2">Preview with sample data</p>
                {preview ? (
                  <>
                    <p className="text-sm font-medium text-zinc-800 mb-2" data-testid="preview-subject">{preview.subject}</p>
                    <iframe title="preview" srcDoc={preview.html} data-testid="preview-frame"
                      className="w-full h-[460px] border rounded-md bg-white" />
                  </>
                ) : <div className="h-[460px] border rounded-md flex items-center justify-center text-sm text-zinc-400">Loading preview…</div>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={save} disabled={saving} className="bg-cc-blue text-white" data-testid="save-template-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save wording"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
